import React from "react";
import { Link, useHistory } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";

import MatchNightCard from "../../match-night/MatchNightCard";
import useMatchNights from "../../match-night/useMatchNights";
import { pickNightsForPreferredRegion } from "../../match-night/preferredMatchNight";
import { colors } from "./colors";
import { PageContainer } from "./PageContainer";

const MotionBox = motion(Box);

const HERO_BG = "#080808";

const REVEAL = {
  initial: { opacity: 0, y: 28, filter: "blur(10px)" },
  whileInView: { opacity: 1, y: 0, filter: "blur(0px)" },
  viewport: { once: true, amount: 0.25 },
};

const SECTION_SX = {
  position: "relative",
  paddingTop: "6rem",
  paddingBottom: "6rem",
  px: { xs: 2, md: 4 },
  backgroundColor: HERO_BG,
};

const LABEL_SX = {
  display: "flex",
  alignItems: "center",
  gap: "0.8rem",
  mb: 1.5,
  fontSize: "0.65rem",
  fontWeight: 600,
  letterSpacing: "0.25em",
  textTransform: "uppercase",
  color: colors.primary.main,
  "&::before": {
    content: '""',
    display: "block",
    width: 24,
    height: "1px",
    background: colors.primary.main,
  },
};

const TITLE_SX = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: { xs: "2.5rem", sm: "3.25rem", md: "3.75rem" },
  fontWeight: 700,
  letterSpacing: "-0.04em",
  lineHeight: 0.95,
  textTransform: "uppercase",
  color: colors.text.primary,
  mb: 0,
  "& span": {
    background: `
      linear-gradient(
        180deg,
        #f7cb79 0%,
        #f0b458 40%,
        #de8f2c 100%
      )
    `,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
  },
};

const DESCRIPTION_SX = {
  maxWidth: "34rem",
  fontSize: "0.95rem",
  lineHeight: 1.8,
  color: colors.text.secondary,
};

const gridSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
  gap: { xs: "16px", md: "24px" },
  maxWidth: "45rem",
  mx: "auto",
  mt: { xs: "3rem", md: "5rem" },
};

const emptyTeaserSx = {
  maxWidth: "45rem",
  mx: "auto",
  mt: { xs: "3rem", md: "5rem" },
  p: { xs: "24px 20px", md: "32px 28px" },
  border: `1px solid ${colors.border.default}`,
  borderRadius: "18px",
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  textAlign: "center",
};

const emptyTeaserTextSx = {
  fontSize: "0.95rem",
  color: colors.text.secondary,
  lineHeight: 1.7,
};

const btnGhostSx = {
  fontFamily: "'Inter', system-ui, sans-serif",
  fontSize: "13px",
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: colors.text.secondary,
  px: "32px",
  py: "13px",
  textDecoration: "none",
  border: "1px solid rgba(255,255,255,0.08)",
  background: "linear-gradient(180deg, rgba(255,255,255,0.01), transparent)",
  position: "relative",
  overflow: "hidden",
  display: "inline-block",
  transition:
    "transform .25s cubic-bezier(.22,1,.36,1), border-color .25s ease, color .25s ease, box-shadow .25s ease, background .25s ease",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "15%",
    right: "15%",
    height: "1px",
    background: "linear-gradient(to right, transparent, rgba(255,255,255,0.18), transparent)",
    opacity: 0,
    transition: "opacity .25s ease",
  },
  "&:hover": {
    color: colors.text.primary,
    borderColor: "rgba(232,163,61,0.25)",
    background: "linear-gradient(180deg, rgba(232,163,61,0.04), rgba(232,163,61,0.02))",
    boxShadow: "0 8px 24px rgba(232,163,61,0.08)",
    transform: "translateY(-2px)",
  },
  "&:hover::before": { opacity: 1 },
  "&:active": { transform: "translateY(0px)" },
};

const SUBHEAD =
  "Everyone shows up at the same time, so games are full and the competition is real. Hand-vetted players, balanced matches, servers in EU and NA.";

export default function MatchNightSection() {
  const { nights, preferredRegion, loading } = useMatchNights();
  const history = useHistory();
  const upcomingNights = pickNightsForPreferredRegion(nights, preferredRegion).slice(0, 2);
  // Homepage visitors are always signed out here (authenticated users are
  // redirected to /lobby before this renders), so the CTA sends them to the
  // full match night page to sign in and RSVP rather than opening a dialog.
  const goToMatchNightPage = React.useCallback(() => history.push("/match-night"), [history]);

  return (
    <Box component="section" id="match-night" sx={SECTION_SX}>
      <PageContainer sx={{ maxWidth: "75rem" }}>
        <MotionBox {...REVEAL} transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}>
          <Typography component="div" sx={LABEL_SX}>
            Events
          </Typography>
          <Typography component="h2" sx={TITLE_SX}>
            Match <span>Night</span>
          </Typography>
          <Typography sx={DESCRIPTION_SX}>{SUBHEAD}</Typography>
        </MotionBox>

        {!loading && upcomingNights.length > 0 ? (
          <Box sx={gridSx}>
            {upcomingNights.map((night, index) => (
              <MotionBox
                key={night.id}
                {...REVEAL}
                transition={{ duration: 0.9, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
              >
                <MatchNightCard night={night} onReserve={goToMatchNightPage} />
              </MotionBox>
            ))}
          </Box>
        ) : !loading ? (
          <MotionBox
            {...REVEAL}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            sx={emptyTeaserSx}
          >
            <Typography sx={emptyTeaserTextSx}>
              New match nights are announced regularly. Check back soon to reserve your spot.
            </Typography>
          </MotionBox>
        ) : null}

        <Box sx={{ display: "flex", justifyContent: "center", mt: { xs: 5, md: 6 } }}>
          <Box component={Link} to="/match-night" sx={btnGhostSx}>
            See match nights →
          </Box>
        </Box>
      </PageContainer>
    </Box>
  );
}
